'use client'

import { TaskStatus, TaskActionType } from '@/lib/types/task'
import { CheckCircle2Icon, ClockIcon, XCircleIcon, PlayIcon, AlertCircleIcon } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Streamdown, type Components } from 'streamdown'
import { useTranslation } from 'react-i18next'
import { streamdownStaticProps } from '@/components/messages/streamdown-animation'
import { SafeHtmlContent } from '@/components/utility/safe-html-content'

export const styles = {
  progressBar: {
    base: 'h-2 w-24 rounded-full bg-muted',
    fill: 'h-2 rounded-full bg-primary motion-safe:transition-[width] motion-safe:duration-500 motion-reduce:transition-none'
  },
  markdownLink:
    'inline-block max-w-[200px] truncate align-bottom text-primary hover:text-primary/80',
  deleteButton: 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
} as const

export const getStatusIcon = (status: TaskStatus) => {
  switch (status) {
    case 'completed':
      return <CheckCircle2Icon className="text-emerald-500" size={20} />
    case 'in_progress':
      return <PlayIcon className="text-amber-500" size={20} />
    case 'pending':
      return <ClockIcon className="text-muted-foreground" size={20} />
    case 'failed':
      return <XCircleIcon className="text-destructive" size={20} />
    default:
      return <AlertCircleIcon className="text-muted-foreground" size={20} />
  }
}

export const formatStatus = (
  status: TaskStatus,
  translate: (key: string) => string = (key: string) => key
) => {
  switch (status) {
    case 'in_progress':
      return translate('In Progress')
    case 'pending':
      return translate('Pending')
    case 'completed':
      return translate('Completed')
    case 'failed':
      return translate('Failed')
    default:
      return translate(status)
  }
}

export const formatActionName = (actionFunction: string, t: (key: string) => string) => {
  if (!actionFunction) return t('Unknown Action')

  const specialCases: Record<string, string> = {
    web_scrape: t('Scrape Web'),
    process_ai: t('Process with Losi AI'),
    generate_chart: t('Generate Chart'),
    generate_pdf: t('Generate PDF'),
    generate_image: t('Generate Image'),
    generate_google_sheet: t('Generate Google Sheet'),
    generate_ical: t('Generate iCal'),
    generate_google_doc: t('Generate Google Doc'),
    send_email: t('Send Email')
  }

  return actionFunction in specialCases
    ? specialCases[actionFunction]
    : actionFunction
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
}

export const formatDateTime = (
  date: Date,
  translate: (key: string) => string = (key: string) => key
) => {
  const h = date.getHours()
  const m = date.getMinutes()
  const period = h >= 12 ? translate('PM') : translate('AM')
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h
  const timeStr = `${hour12}${m > 0 ? ':' + m.toString().padStart(2, '0') : ''} ${period}`

  const months = [
    translate('January'),
    translate('February'),
    translate('March'),
    translate('April'),
    translate('May'),
    translate('June'),
    translate('July'),
    translate('August'),
    translate('September'),
    translate('October'),
    translate('November'),
    translate('December')
  ]
  const month = months[date.getMonth()]
  const day = date.getDate()

  const getOrdinal = (n: number) => {
    const s = [translate('th'), translate('st'), translate('nd'), translate('rd')]
    const v = n % 100
    return n + (s[(v - 20) % 10] || s[v] || s[0])
  }

  return `${timeStr} on ${month} ${getOrdinal(day)}`
}

export const createFormatValue = (
  expandedKeys: Record<string, boolean>,
  toggleExpand: (key: string) => void,
  markdownComponents: Components,
  translate: (key: string) => string
) => {
  const formatValue = (key: string, value: any): JSX.Element => {
    // Handle notification specific fields
    if (key === 'content' || key === 'message' || key === 'body') {
      const isHTML = typeof value === 'string' && (value.includes('</') || value.includes('/>'))
      const isMarkdown =
        typeof value === 'string' &&
        (value.includes('#') ||
          value.includes('**') ||
          value.includes('```') ||
          value.includes('- ') ||
          value.includes('* ') ||
          value.includes('[') ||
          value.includes('|'))
      const isLong = typeof value === 'string' && value.length > 150
      const isExpanded = expandedKeys[key]

      return (
        <div className="flex max-w-full flex-col gap-1">
          <span className="text-sm text-muted-foreground">{translate('Message')}:</span>
          <div className="max-w-full overflow-hidden rounded-lg border border-border/70 bg-card p-2 text-sm">
            {isHTML ? (
              <SafeHtmlContent
                className={`prose prose-sm max-w-none break-words dark:prose-invert ${!isExpanded ? 'line-clamp-3' : ''}`}
                html={value}
              />
            ) : isMarkdown ? (
              <div className={!isExpanded ? 'line-clamp-3' : ''}>
                <Streamdown
                  {...streamdownStaticProps}
                  className="prose prose-sm max-w-none break-words dark:prose-invert"
                  components={markdownComponents}
                >
                  {value}
                </Streamdown>
              </div>
            ) : (
              <div className={`break-words ${!isExpanded ? 'line-clamp-3' : ''}`}>{value}</div>
            )}
            {isLong && (
              <button
                onClick={() => toggleExpand(key)}
                className="mt-1 text-xs text-primary hover:underline"
              >
                {isExpanded ? translate('Show less') : translate('Show more')}
              </button>
            )}
          </div>
        </div>
      )
    }

    if (key === 'subject' || key === 'title') {
      return (
        <div className="flex flex-col gap-1">
          <span className="text-sm text-muted-foreground">{translate('Subject')}:</span>
          <div className="text-sm font-medium">{value}</div>
        </div>
      )
    }

    if (key === 'attachments') {
      const files = Array.isArray(value) ? value : [value]
      const isLong = files.length > 3
      const isExpanded = expandedKeys[key]
      const displayFiles = isExpanded ? files : files.slice(0, 3)

      return (
        <div className="flex flex-col gap-1">
          <span className="text-sm text-muted-foreground">{translate('Attachments')}:</span>
          <div className="flex flex-wrap gap-1">
            {displayFiles.map((file, i) => (
              <Badge key={i} variant="outline" className="text-xs">
                {typeof file === 'string' ? file : file.name || translate('File')}
              </Badge>
            ))}
            {isLong && !isExpanded && (
              <button
                onClick={() => toggleExpand(key)}
                className="text-xs text-primary hover:underline"
              >
                +{files.length - 3} {translate('more')}
              </button>
            )}
            {isLong && isExpanded && (
              <button
                onClick={() => toggleExpand(key)}
                className="text-xs text-primary hover:underline"
              >
                {translate('Show less')}
              </button>
            )}
          </div>
        </div>
      )
    }

    // Handle URL or link fields
    if (key.includes('url') || key.includes('link')) {
      return (
        <div className="flex max-w-full flex-col gap-1">
          <span className="text-sm text-muted-foreground">
            {key.charAt(0).toUpperCase() + key.slice(1)}:
          </span>
          <div className="max-w-full overflow-hidden">
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.markdownLink}
            >
              {value}
            </a>
          </div>
        </div>
      )
    }

    // Handle date fields
    if (key.includes('date') || key.includes('time')) {
      try {
        const date = new Date(value)
        if (!isNaN(date.getTime())) {
          return (
            <div className="flex flex-col gap-1">
              <span className="text-sm text-muted-foreground">
                {key.charAt(0).toUpperCase() + key.slice(1)}:
              </span>
              <div className="text-sm">{formatDateTime(date, translate)}</div>
            </div>
          )
        }
      } catch (e) {
        // If date parsing fails, fall through to default handling
      }
    }

    // Default handling for other types
    if (typeof value === 'object' && value !== null) {
      return (
        <div className="flex max-w-full flex-col gap-1">
          <span className="text-sm text-muted-foreground">
            {key.charAt(0).toUpperCase() + key.slice(1)}:
          </span>
          <div className="max-w-full space-y-2 overflow-hidden">
            {Object.entries(value).map(([subKey, subValue], i) => (
              <div key={i} className="ml-2">
                {formatValue(subKey, subValue)}
              </div>
            ))}
          </div>
        </div>
      )
    }

    return (
      <div className="flex max-w-full flex-col gap-1">
        <span className="text-sm text-muted-foreground">
          {key.charAt(0).toUpperCase() + key.slice(1)}:
        </span>
        <div className="break-words text-sm">{String(value)}</div>
      </div>
    )
  }

  formatValue.displayName = 'FormatValue'
  return formatValue
}
